import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import InputTextControl from "~/components/InputTextControl";
import InputTextAreaControl from "~/components/InputTextAreaControl";
import lists from "~/lib/lists";
import InputSelectControl from "~/components/InputSelectControl";
import InputMemberSelectControl from "~/components/InputMemberSelectControl";
import InputToggleControl from "~/components/InputToggleControl";
import { Label, Grid, Text, Icon } from "~/components-shared";
import styled from "styled-components";
import InputDateControl from "~/components/InputDateControl";
import { AuthoritiesEnum, RoutesEnum, TopicFootprintSourceEnum, TopicRetroPeriodEnum, TopicVisibilityEnum } from "~/types/enums";
import { useIntegratedSources } from "~/hooks/topics";
import InputJiraProjectSelectControl from "~/components/InputJiraProjectSelectControl";
import InputSlackChannelSelectControl from "~/components/InputSlackChannelSelectControl";
import InputLinearTeamsSelectControl from "~/components/InputLinearTeamsSelectControl";
import InputAsanaProjectsSelectControl from "~/components/InputAsanaProjectsSelectControl";
import InputGithubRepositorySelectControl from "~/components/InputGithubRepositorySelectControl";
import useAbacExt from "~/hooks/abac";
import api from "~/lib/api";
import InputSalesforceOpportunitySelectControl from "~/components/InputSalesforceOpportunitySelectControl";
import Button from "../../components-shared/Button";
const StyledGrid = styled(Grid)`
  flex-grow: 1;
  height: 100%;
`;
const StyledGridCell = styled(Grid.Cell)`
  overflow: auto;
  height: 100%;
`;
const TopicForm = _ref => {
  let {
    topic = null,
    control,
    watch,
    setValue,
    fromTemplate,
    singleColumn
  } = _ref;
  const {
    t
  } = useTranslation();
  const activeSources = useIntegratedSources();
  const hasPrivateChannels = watch("settings.hasPrivateChannels");
  const [slackChannels, setSlackCannels] = useState([]);
  const [slackChannelsLoading, setSlackCannelsLoading] = useState(false);
  const {
    userHas
  } = useAbacExt();
  const sources = Object.keys(lists.topics.footprintSources.names).map(source => ({
    id: source,
    value: lists.topics.footprintSources.names[source],
    hidden: !activeSources.includes(source)
  }));
  const visibilities = Object.keys(lists.topics.visibility.names).map(key => {
    const hidePrivate = key === TopicVisibilityEnum.PRIVATE && topic !== null && !topic.isCreator;
    const hidePrivateSlack = key === TopicVisibilityEnum.EVERYONE && hasPrivateChannels;
    return {
      id: key,
      value: lists.topics.visibility.names[key],
      tip: lists.topics.visibility.descriptions[key],
      hidden: hidePrivate || hidePrivateSlack
    };
  });
  useEffect(() => {
    if (userHas(AuthoritiesEnum.EXPERIMENTAL)) {
      setSlackCannelsLoading(true);
      api.fetchSlackChannels().then(response => {
        setSlackCannels(response);
      }).finally(() => {
        setSlackCannelsLoading(false);
      });
    }
  }, []);
  return /*#__PURE__*/React.createElement(StyledGrid, {
    cols: singleColumn ? 1 : 2,
    equal: true
  }, /*#__PURE__*/React.createElement(StyledGridCell, {
    s: singleColumn ? {
      px: 1.5,
      mb: 5
    } : {
      pl: 1.5,
      pr: 3
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    s: {
      mb: 3
    }
  }, t("TopicForm.generalSettings")), fromTemplate ? /*#__PURE__*/React.createElement(InputTextControl, {
    name: "name",
    label: t("TopicForm.title"),
    control: control,
    s: {
      mb: 5
    },
    required: true,
    emojiPicker: true
  }) : /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "auto 150px",
    gap: 3,
    s: {
      mb: 5
    }
  }, /*#__PURE__*/React.createElement(InputTextControl, {
    name: "name",
    label: t("TopicForm.title"),
    control: control,
    required: true,
    emojiPicker: true
  }), /*#__PURE__*/React.createElement(InputSelectControl, {
    name: "status",
    options: Object.keys(lists.topics.statuses.names).map(key => ({
      id: key,
      value: lists.topics.statuses.names[key],
      tip: lists.topics.statuses.descriptions[key]
    })),
    label: t("TopicForm.status"),
    control: control,
    maxWidth: "150px",
    required: true
  })), /*#__PURE__*/React.createElement(Label, {
    s: {
      mb: 2
    },
    required: true
  }, t("TopicForm.description")), /*#__PURE__*/React.createElement(Icon, {
    s: {
      ml: 0.5,
      mt: -0.5
    },
    color: "secondary",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": "The more <strong>precise</strong> the description is, the more <strong>accurately</strong> AI can recognize the Footprints associated with it.",
    style: {
      cursor: "help"
    },
    src: "infoOutline"
  }), /*#__PURE__*/React.createElement(InputTextAreaControl, {
    name: "description",
    rows: 5,
    s: {
      mb: 5
    },
    control: control,
    emojiPicker: true
  }), /*#__PURE__*/React.createElement(Label, {
    s: {
      mb: 2
    },
    required: true
  }, t("TopicForm.keywords")), /*#__PURE__*/React.createElement(Icon, {
    s: {
      ml: 0.5,
      mt: -0.5
    },
    color: "secondary",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": "Define <strong>keywords</strong> to help our AI detect relevant topic activity",
    style: {
      cursor: "help"
    },
    src: "infoOutline"
  }), /*#__PURE__*/React.createElement(InputTextControl, {
    name: "keywords",
    s: {
      mb: 5
    },
    control: control,
    required: true
  }), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "220px 1fr",
    gap: 3,
    s: {
      mb: 5
    }
  }, /*#__PURE__*/React.createElement(InputSelectControl, {
    name: "visibility",
    label: t("TopicForm.visibility"),
    options: visibilities,
    control: control,
    required: true
  }), /*#__PURE__*/React.createElement(InputDateControl, {
    name: "startDate",
    label: t("TopicForm.startDate"),
    control: control,
    required: true
  })), watch("visibility") !== TopicVisibilityEnum.PRIVATE && /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 3,
    template: "220px 1fr"
  }, /*#__PURE__*/React.createElement(InputMemberSelectControl, {
    name: "ownerId",
    label: t("TopicForm.owner"),
    placeholder: "Select Member...",
    defaultMember: topic?.owner,
    control: control,
    required: true
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Label, {
    s: {
      mb: 2
    }
  }, t("TopicForm.stakeholders")), /*#__PURE__*/React.createElement(Icon, {
    s: {
      ml: 0.5,
      mt: -0.5
    },
    color: "secondary",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": "<strong>Stakeholder</strong> members with permission to view topic progress and updates.",
    src: "infoOutline",
    style: {
      cursor: "help"
    }
  }), /*#__PURE__*/React.createElement(InputMemberSelectControl, {
    name: "memberIds",
    filterMembers: [watch("ownerId")],
    defaultMembers: topic?.members,
    control: control,
    placeholder: "Select Members...",
    s: {
      mb: 3
    },
    fullSelections: true,
    multiple: true
  })))), /*#__PURE__*/React.createElement(StyledGridCell, {
    s: singleColumn ? {
      px: 1.5
    } : {
      pl: 3,
      pr: 3
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    s: {
      mb: 3
    }
  }, t("TopicForm.footprintDetection")), /*#__PURE__*/React.createElement(Label, {
    s: {
      mb: 2
    }
  }, t("TopicForm.sources")), /*#__PURE__*/React.createElement(Icon, {
    s: {
      ml: 0.5,
      mt: -0.5
    },
    color: "secondary",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": "The <strong>tools</strong> and <strong>data sources</strong> where activity can be detected",
    style: {
      cursor: "help"
    },
    src: "infoOutline"
  }), /*#__PURE__*/React.createElement(InputSelectControl, {
    name: "sources",
    options: sources,
    s: {
      mb: 5
    },
    control: control,
    placeholder: "Select Detection Sources...",
    noOptionsText: `There are **no** available Sources available.\n\n Please follow the [Account Settings](${RoutesEnum.ACCOUNT_SETTINGS}) section\n\n to enable integrations.`,
    fullSelections: true,
    multiple: true
  }), !!watch("sources").length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Label, {
    s: {
      mb: 2
    },
    required: true
  }, t("TopicForm.uniqueTag")), /*#__PURE__*/React.createElement(Icon, {
    s: {
      ml: 0.5,
      mt: -0.5
    },
    color: "secondary",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": "Unique <strong>hashtag</strong> used to explicitly tag relevant messages, events, tickets, and content. <br/><br/>Use this tag in your <strong>title</strong>, <strong>description</strong>, <strong>message</strong> or <strong>comment</strong> to force-map Footprint under specific Topic. <br/><br/>Better to keep it unique per Topic. For example: <strong>#joyn_okr</strong>",
    style: {
      cursor: "help"
    },
    src: "infoOutline"
  }), /*#__PURE__*/React.createElement(InputTextControl, {
    elemBefore: /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      weight: "light"
    }, "#"),
    prefix: "joyn_",
    name: "trigger",
    s: {
      mb: 5
    },
    control: control,
    maxWidth: "200px",
    required: true
  }), watch("sources").includes(TopicFootprintSourceEnum.SLACK) && /*#__PURE__*/React.createElement(InputSlackChannelSelectControl, {
    label: t("TopicForm.slackChannels"),
    name: "settings.slackChannels",
    placeholder: "Select Channels...",
    s: {
      mb: 5
    },
    multiple: true,
    onPrivateChange: channels => {
      setValue("settings.hasPrivateChannels", !!channels.length);
    },
    control: control,
    fullSelections: true,
    showPrivateHint: true,
    required: true
  }), watch("sources").includes(TopicFootprintSourceEnum.SLACK) && userHas(AuthoritiesEnum.EXPERIMENTAL) && /*#__PURE__*/React.createElement(InputSelectControl, {
    label: /*#__PURE__*/React.createElement(React.Fragment, null, t("TopicForm.slackDirectChannels"), /*#__PURE__*/React.createElement(Icon, {
      src: "encrypted",
      s: {
        ml: 1
      },
      color: "primary"
    })),
    name: "settings.slackDirectChannels",
    placeholder: "Select Channels...",
    options: watch("settings.slackChannels")?.map(channel => {
      const channelName = slackChannels.find(c => c.id === channel)?.name;
      return channelName ? {
        id: channel,
        value: `#${channelName}`
      } : {
        id: channel,
        value: channel
      };
    }) || [],
    s: {
      mb: 5
    },
    loading: slackChannelsLoading,
    multiple: true,
    control: control,
    fullSelections: true
  }), watch("sources").includes(TopicFootprintSourceEnum.JIRA) && /*#__PURE__*/React.createElement(InputJiraProjectSelectControl, {
    label: t("TopicForm.jiraProjects"),
    name: "settings.jiraProjects",
    placeholder: "Select Projects...",
    s: {
      mb: 5
    },
    multiple: true,
    control: control,
    fullSelections: true,
    required: true
  }), watch("sources").includes(TopicFootprintSourceEnum.LINEAR) && /*#__PURE__*/React.createElement(InputLinearTeamsSelectControl, {
    label: t("TopicForm.linearTeams"),
    name: "settings.linearTeams",
    placeholder: "Select Teams...",
    s: {
      mb: 5
    },
    multiple: true,
    control: control,
    fullSelections: true,
    required: true
  }), watch("sources").includes(TopicFootprintSourceEnum.ASANA) && /*#__PURE__*/React.createElement(InputAsanaProjectsSelectControl, {
    label: t("TopicForm.asanaProjects"),
    name: "settings.asanaProjects",
    placeholder: "Select Projects...",
    s: {
      mb: 5
    },
    multiple: true,
    control: control,
    fullSelections: true,
    required: true
  }), watch("sources").includes(TopicFootprintSourceEnum.GITHUB) && /*#__PURE__*/React.createElement(InputGithubRepositorySelectControl, {
    label: t("TopicForm.githubRepositories"),
    name: "settings.githubRepositories",
    placeholder: "Select Repositories...",
    s: {
      mb: 5
    },
    multiple: true,
    control: control,
    fullSelections: true,
    required: true
  }), watch("sources").includes(TopicFootprintSourceEnum.SALESFORCE) && userHas(AuthoritiesEnum.EXPERIMENTAL) && /*#__PURE__*/React.createElement(InputSalesforceOpportunitySelectControl, {
    label: /*#__PURE__*/React.createElement(React.Fragment, null, t("TopicForm.salesforceRecords"), /*#__PURE__*/React.createElement(Icon, {
      src: "encrypted",
      s: {
        ml: 1
      },
      color: "primary"
    })),
    name: "settings.salesforceRecords",
    placeholder: "Select Records...",
    s: {
      mb: 5
    },
    multiple: true,
    control: control,
    fullSelections: true
  }), watch("sources").includes(TopicFootprintSourceEnum.SLACK) && /*#__PURE__*/React.createElement(InputToggleControl, {
    name: "markSlackAiDetections",
    label: /*#__PURE__*/React.createElement(React.Fragment, null, t("TopicForm.markSlackAiDetections"), /*#__PURE__*/React.createElement(Icon, {
      s: {
        ml: 1,
        mt: -0.5
      },
      color: "secondary",
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-html": "Mark detected <strong>Slack</strong> messages with a \u2611\uFE0F <strong>checkbox</strong> emoji reaction",
      style: {
        cursor: "help"
      },
      src: "infoOutline"
    })),
    s: {
      mb: 5
    },
    control: control,
    display: "block"
  }), !topic && watch("sources").length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Label, {
    s: {
      mb: 2
    }
  }, t("TopicForm.retroDetection")), /*#__PURE__*/React.createElement("div", null, Object.keys(lists.topics.retroPeriod.names).map(id => /*#__PURE__*/React.createElement(Button, {
    key: id,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: id === TopicRetroPeriodEnum.NOT_SET ? "cross" : "timelapse",
      s: {
        mr: 1,
        mt: -0.5
      }
    }),
    color: watch("retroPeriod") === id ? "primary" : "grey",
    display: "inline-block",
    fontSize: 3,
    onClick: () => setValue("retroPeriod", id),
    outline: false,
    s: {
      pl: 2,
      pr: 3,
      py: 1,
      mr: 2,
      mb: 2
    }
  }, lists.topics.retroPeriod.names[id])))))));
};
export default TopicForm;